import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Shield, LayoutDashboard, Inbox, ClipboardList, Building2, BarChart3, Radar, Star,
  ArrowUpRight, LogOut, ChevronDown, Users, TrendingUp, Handshake, Network, RefreshCw,
} from "lucide-react";

const OVERVIEW = { label: "Overview", path: "/admin", end: true, icon: LayoutDashboard, testid: "admin-nav-overview" };
const INTELLIGENCE = { label: "Intelligence", path: "/admin/intelligence", icon: Radar, testid: "admin-nav-intelligence" };
const PARTNERS = { label: "Partners", path: "/admin/premier-partners", icon: Handshake, testid: "admin-nav-partners" };

const DEALERS_ITEMS = [
  { label: "Accounts", path: "/admin/dealers", icon: Building2, testid: "admin-nav-dealers" },
  { label: "Dealer Groups", path: "/admin/dealer-groups", icon: Network, testid: "admin-nav-dealer-groups" },
  { label: "Usage", path: "/admin/usage", icon: BarChart3, testid: "admin-nav-usage" },
  { label: "Inventory Sync", path: "/admin/inventory-sync", icon: RefreshCw, testid: "admin-nav-inventory-sync" },
];
const GROWTH_ITEMS = [
  { label: "Demo Requests", path: "/admin/demo-requests", icon: Inbox, testid: "admin-nav-demo-requests", badge: true },
  { label: "Assessments", path: "/admin/assessments", icon: ClipboardList, testid: "admin-nav-assessments" },
];

const ALL_MOBILE = [OVERVIEW, ...DEALERS_ITEMS, ...GROWTH_ITEMS, INTELLIGENCE, PARTNERS];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [newCount, setNewCount] = useState(0);

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/admin/new-demo-count")
      .then(({ data }) => { if (alive) setNewCount(data.new || 0); })
      .catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const groupActive = (paths) => paths.some((p) => pathname === p || pathname.startsWith(p + "/"));

  const Badge = ({ extra = "" }) => newCount > 0 ? (
    <span data-testid="admin-new-demo-badge"
      className={`min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold inline-flex items-center justify-center ${extra}`}>
      {newCount > 99 ? "99+" : newCount}
    </span>
  ) : null;

  const topLinkCls = ({ isActive }) =>
    `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${isActive ? "bg-gold text-navy" : "text-slate-300 hover:text-white hover:bg-white/10"}`;

  const triggerCls = (active) =>
    `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-colors outline-none ${active ? "bg-gold text-navy" : "text-slate-300 hover:text-white hover:bg-white/10"}`;

  const NavGroup = ({ label, icon: Icon, items, testid }) => {
    const active = groupActive(items.map((i) => i.path));
    return (
      <DropdownMenu>
        <DropdownMenuTrigger data-testid={testid} className={triggerCls(active)}>
          <Icon className="w-4 h-4" /> {label}
          {label === "Growth" && <Badge />}
          <ChevronDown className="w-3.5 h-3.5 opacity-70" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="bg-white min-w-[200px]">
          <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">{label}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {items.map((it) => (
            <DropdownMenuItem key={it.path} data-testid={`${it.testid}-menu`} onClick={() => navigate(it.path)}
              className="gap-2 cursor-pointer font-medium">
              <it.icon className="w-4 h-4 text-navy" /> {it.label}
              {it.badge && <Badge extra="ml-auto" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const initials = (user?.email || "A").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen bg-background" data-testid="admin-layout">
      <header className="sticky top-0 z-50 glass-navy border-b-2 border-gold/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16 gap-6">
            {/* Brand lockup */}
            <div className="flex items-center gap-3 shrink-0" data-testid="admin-brand">
              <div className="w-10 h-10 rounded-xl gold-gradient flex items-center justify-center shadow-sm">
                <Shield className="w-5 h-5 text-navy" />
              </div>
              <div className="flex items-center gap-2.5">
                <span className="text-lg font-bold text-white tracking-tight leading-none">Marine Ad AI</span>
                <span className="hidden sm:inline-block text-[10px] font-bold uppercase tracking-[0.2em] text-navy bg-gold rounded px-1.5 py-0.5 leading-none">Admin</span>
              </div>
            </div>

            {/* Primary nav */}
            <nav className="hidden md:flex items-center gap-1 flex-1 justify-center lg:justify-start">
              <NavLink to={OVERVIEW.path} end data-testid={OVERVIEW.testid} className={topLinkCls}>
                <OVERVIEW.icon className="w-4 h-4" /> {OVERVIEW.label}
              </NavLink>
              <NavGroup label="Dealers" icon={Users} items={DEALERS_ITEMS} testid="admin-nav-dealers-group" />
              <NavGroup label="Growth" icon={TrendingUp} items={GROWTH_ITEMS} testid="admin-nav-growth-group" />
              <NavLink to={INTELLIGENCE.path} data-testid={INTELLIGENCE.testid} className={topLinkCls}>
                <INTELLIGENCE.icon className="w-4 h-4" /> {INTELLIGENCE.label}
              </NavLink>
              <NavLink to={PARTNERS.path} data-testid={PARTNERS.testid} className={topLinkCls}>
                <PARTNERS.icon className="w-4 h-4" /> {PARTNERS.label}
              </NavLink>
            </nav>

            {/* Utility + account */}
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => navigate("/dashboard")} data-testid="admin-back-to-app"
                className="flex items-center gap-1.5 text-sm font-semibold text-gold/90 hover:text-gold border border-gold/40 hover:border-gold/70 px-3 py-1.5 rounded-lg transition-colors">
                <ArrowUpRight className="w-4 h-4" /> <span className="hidden sm:inline">Dealer App</span>
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger data-testid="admin-account-menu"
                  className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs font-bold flex items-center justify-center transition-colors outline-none">
                  {initials}
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white min-w-[220px]">
                  <DropdownMenuLabel className="font-normal">
                    <div className="text-sm font-semibold text-navy truncate">{user?.email}</div>
                    <div className="text-xs text-muted-foreground capitalize">{user?.role}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="admin-logout" onClick={logout}
                    className="gap-2 cursor-pointer text-red-600 focus:text-red-600 font-medium">
                    <LogOut className="w-4 h-4" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Mobile nav */}
          <nav className="md:hidden flex items-center gap-1 pb-3 overflow-x-auto" data-testid="admin-mobile-nav">
            {ALL_MOBILE.map((l) => (
              <NavLink key={l.path} to={l.path} end={l.end}
                className={({ isActive }) => `flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap ${isActive ? "bg-gold text-navy" : "text-slate-300 hover:bg-white/10"}`}>
                <l.icon className="w-3.5 h-3.5" /> {l.label} {l.badge && <Badge />}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <Outlet />
      </main>
    </div>
  );
}
